import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserCheck, Clock, Shield, ChevronDown, Trash2, AlertOctagon, Layers } from "lucide-react";
import {
  ADMIN_LIKE,
  CATEGORIES,
  CATEGORY_LABEL,
  ROLE_LABEL,
  isAdminLike,
} from "@/lib/roles";

const ROLE_OPTIONS = [
  { value: "player", label: "Jugador" },
  { value: "coach", label: "Coach" },
  { value: "admin_directive", label: "Admin Directivo" },
  { value: "admin_treasurer", label: "Admin Tesorero" },
  { value: "super_admin", label: "Super Admin" },
  { value: "pending", label: "Pendiente" },
];

// Roles que un usuario puede tener como secundarios (no incluye "pending")
const EXTRA_ROLE_OPTIONS = [
  { value: "player", label: "Jugador" },
  { value: "coach", label: "Coach" },
  { value: "admin_directive", label: "Admin Directivo" },
  { value: "admin_treasurer", label: "Admin Tesorero" },
  { value: "super_admin", label: "Super Admin" },
];

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editCats, setEditCats] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [extraRolesFor, setExtraRolesFor] = useState(null); // user being edited
  const [extraRolesSel, setExtraRolesSel] = useState([]);

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/stats/club")]);
    setUsers(u.data);
    setStats(s.data);
  };
  useEffect(() => {
    load();
  }, []);

  const pendingUsers = useMemo(() => users.filter((u) => u.role === "pending"), [users]);
  const otherUsers = useMemo(() => users.filter((u) => u.role !== "pending"), [users]);

  if (!isAdminLike(user?.role)) {
    return (
      <Layout>
        <div className="p-10 text-[#A1A1AA]" data-testid="admin-no-access">
          Sin acceso.
        </div>
      </Layout>
    );
  }

  const changeRole = async (uid, role, coachCats = null) => {
    const body = { role };
    if (coachCats !== null) body.coach_categories = coachCats;
    await api.patch(`/users/${uid}/role`, body);
    toast.success("Rol actualizado");
    setEditingId(null);
    load();
  };

  const toggleCat = (c) =>
    setEditCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const saveCoachCats = async (uid) => {
    // Mantener el rol primario actual (puede ser coach o admin con coach como rol secundario)
    const target = users.find(u => u.user_id === uid);
    const body = { role: target?.role || "coach", coach_categories: editCats };
    if (target?.authorized_roles && target.authorized_roles.length) {
      body.authorized_roles = (target.authorized_roles || []).filter(r => r !== target.role);
    }
    await api.patch(`/users/${uid}/role`, body);
    toast.success("Categorías de coach actualizadas");
    setEditingId(null);
    load();
  };

  const deleteUser = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/users/${toDelete.user_id}`);
      toast.success(`Usuario "${r.data.deleted_user || toDelete.name}" eliminado`);
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar usuario");
    } finally { setDeleting(false); }
  };

  const openExtraRoles = (u) => {
    setExtraRolesFor(u);
    // Filtrar el rol primario de la lista de "extras"
    const extras = (u.authorized_roles || []).filter((r) => r !== u.role);
    setExtraRolesSel(extras);
  };

  const toggleExtraRole = (r) => {
    setExtraRolesSel((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const saveExtraRoles = async () => {
    if (!extraRolesFor) return;
    try {
      // El backend exige también el rol actual; el primary_role se mantiene.
      const body = { role: extraRolesFor.role, authorized_roles: extraRolesSel };
      if (extraRolesFor.role === "coach") {
        body.coach_categories = extraRolesFor.coach_categories || [];
      }
      await api.patch(`/users/${extraRolesFor.user_id}/role`, body);
      toast.success("Roles adicionales actualizados");
      setExtraRolesFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar roles");
    }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="mb-8">
          <div className="overline">Command</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">PANEL ADMIN</h1>
          <p className="text-[#A1A1AA] mt-2 text-sm">
            Gestión de usuarios, roles jerárquicos y métricas del club.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-[#27272A] mb-8">
          <Cell label="Usuarios" value={stats?.total_users ?? "—"} />
          <Cell label="Jugadores" value={stats?.total_players ?? "—"} />
          <Cell label="Coaches" value={stats?.total_coaches ?? "—"} />
          <Cell label="Eventos próximos" value={stats?.upcoming_events ?? "—"} />
          <Cell label="Cuotas pagadas" value={stats?.dues_paid ?? "—"} color="var(--success)" />
          <Cell label="Cuotas pendientes" value={stats?.dues_pending ?? "—"} color="var(--warning)" />
        </div>

        {/* Pending approvals */}
        {pendingUsers.length > 0 && (
          <div
            className="tactical-card p-6 mb-6"
            style={{
              borderColor: "#FF9F0A",
              background: "linear-gradient(120deg, rgba(255,159,10,0.08) 0%, transparent 50%)",
            }}
            data-testid="pending-approvals"
          >
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-[#FF9F0A]" />
              <div className="overline text-[#FF9F0A]">
                Solicitudes pendientes ({pendingUsers.length})
              </div>
            </div>
            <div className="space-y-3">
              {pendingUsers.map((u) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between gap-3 flex-wrap border border-[#27272A] bg-[#0A0A0A] p-4"
                  data-testid={`pending-row-${u.user_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-11 h-11 object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-11 h-11 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white font-bold flex-shrink-0">
                        {u.name?.[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display text-lg text-white truncate">
                        {u.name?.toUpperCase()}
                      </div>
                      <div className="text-xs text-[#A1A1AA] truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => changeRole(u.user_id, "player")}
                      className="btn-primary text-xs flex items-center gap-1.5"
                      data-testid={`approve-player-${u.user_id}`}
                      title="Aprobar como Jugador"
                    >
                      <UserCheck size={12} /> Jugador
                    </button>
                    <button
                      onClick={() => changeRole(u.user_id, "coach", [])}
                      className="btn-ghost text-xs flex items-center gap-1.5 border border-[#007AFF] text-[#007AFF] px-3 py-2"
                      data-testid={`approve-coach-${u.user_id}`}
                    >
                      <UserCheck size={12} /> Coach
                    </button>
                    <button
                      onClick={() => changeRole(u.user_id, "admin_directive")}
                      className="btn-ghost text-xs flex items-center gap-1.5 border border-[#A78BFA] text-[#A78BFA] px-3 py-2"
                      data-testid={`approve-directive-${u.user_id}`}
                    >
                      <Shield size={12} /> Directivo
                    </button>
                    <button
                      onClick={() => changeRole(u.user_id, "admin_treasurer")}
                      className="btn-ghost text-xs flex items-center gap-1.5 border border-[#34C759] text-[#34C759] px-3 py-2"
                      data-testid={`approve-treasurer-${u.user_id}`}
                    >
                      <Shield size={12} /> Tesorero
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="tactical-card overflow-x-auto">
          <div className="p-6 border-b border-[#27272A]">
            <div className="overline">Gestión de Roles Jerárquicos</div>
            <div className="font-display text-2xl text-white mt-1">USUARIOS</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#27272A]">
                <th className="text-left p-4 overline">Nombre</th>
                <th className="text-left p-4 overline">Email</th>
                <th className="text-left p-4 overline">Rol actual</th>
                <th className="text-left p-4 overline">Categorías (Coach)</th>
                <th className="text-left p-4 overline">Roles adicionales</th>
                <th className="text-right p-4 overline">Cambiar a</th>
                <th className="text-right p-4 overline">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {otherUsers.map((u) => {
                const editing = editingId === u.user_id;
                const cats = u.coach_categories || [];
                return (
                  <tr
                    key={u.user_id}
                    className="border-b border-[#27272A] hover:bg-[#121212] align-top"
                    data-testid={`user-row-${u.user_id}`}
                  >
                    <td className="p-4 text-white">
                      <div className="flex items-center gap-2">
                        {u.picture && <img src={u.picture} alt="" className="w-7 h-7 object-cover" />}
                        {u.name}
                      </div>
                    </td>
                    <td className="p-4 text-[#A1A1AA] text-xs">{u.email}</td>
                    <td className="p-4">
                      <span className={`badge badge-${u.role}`}>{ROLE_LABEL[u.role] || u.role}</span>
                    </td>
                    <td className="p-4">
                      {/* Mostrar editor de categorías SI tiene "coach" como rol primario O secundario */}
                      {!(u.role === "coach" || (u.authorized_roles || []).includes("coach")) ? (
                        <span className="text-[10px] text-[#52525b]">—</span>
                      ) : editing ? (
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-1.5">
                            {CATEGORIES.map((c) => (
                              <button
                                key={c}
                                onClick={() => toggleCat(c)}
                                data-testid={`coach-cat-${u.user_id}-${c}`}
                                className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${
                                  editCats.includes(c)
                                    ? "border-[#007AFF] bg-[#1A1A1A] text-white"
                                    : "border-[#27272A] text-[#A1A1AA]"
                                }`}
                              >
                                {CATEGORY_LABEL[c]}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => saveCoachCats(u.user_id)}
                              className="btn-primary text-[10px] px-2 py-1"
                              data-testid={`save-coach-cats-${u.user_id}`}
                            >
                              GUARDAR
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="btn-ghost text-[10px]"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {cats.length === 0 && (
                            <span className="text-[10px] text-[#52525b]">Sin asignar</span>
                          )}
                          {cats.map((c) => (
                            <span key={c} className="badge badge-coach text-[10px]">
                              {CATEGORY_LABEL[c]}
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              setEditingId(u.user_id);
                              setEditCats(cats);
                            }}
                            className="text-[10px] text-[#007AFF] hover:text-white underline-offset-2 hover:underline ml-1"
                            data-testid={`edit-coach-cats-${u.user_id}`}
                          >
                            editar
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <ExtraRolesCell u={u} onEdit={() => openExtraRoles(u)}/>
                    </td>
                    <td className="p-4 text-right">
                      <select
                        className="input-tactical inline-block w-auto"
                        value={u.role}
                        onChange={(e) => changeRole(u.user_id, e.target.value)}
                        data-testid={`role-select-${u.user_id}`}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      {u.user_id === user?.user_id ? (
                        <span className="text-[10px] text-[#52525b] italic">(tú)</span>
                      ) : (
                        <button
                          onClick={() => setToDelete(u)}
                          className="btn-ghost text-[10px] flex items-center gap-1 border border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 ml-auto px-2 py-1.5"
                          data-testid={`delete-user-${u.user_id}`}
                          title="Eliminar usuario"
                        >
                          <Trash2 size={11}/> Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="h-20 md:h-6" />
      </div>

      {toDelete && (
        <DeleteUserModal
          user={toDelete}
          busy={deleting}
          onCancel={() => setToDelete(null)}
          onConfirm={deleteUser}
        />
      )}

      {extraRolesFor && (
        <ExtraRolesModal
          user={extraRolesFor}
          selected={extraRolesSel}
          onToggle={toggleExtraRole}
          onClose={() => setExtraRolesFor(null)}
          onSave={saveExtraRoles}
        />
      )}
    </Layout>
  );
}

function ExtraRolesCell({ u, onEdit }) {
  const extras = (u.authorized_roles || []).filter((r) => r !== u.role);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {extras.length === 0 && (
        <span className="text-[10px] text-[#52525b]">Solo rol primario</span>
      )}
      {extras.map((r) => {
        const opt = EXTRA_ROLE_OPTIONS.find((x) => x.value === r);
        return (
          <span key={r} className={`badge badge-${r} text-[10px]`}>
            {opt?.label || r}
          </span>
        );
      })}
      <button
        onClick={onEdit}
        data-testid={`edit-extra-roles-${u.user_id}`}
        className="text-[10px] text-[#22d3ee] hover:text-white underline-offset-2 hover:underline ml-1 flex items-center gap-1"
      >
        <Layers size={10}/> {extras.length ? "editar" : "agregar"}
      </button>
    </div>
  );
}

function ExtraRolesModal({ user, selected, onToggle, onClose, onSave }) {
  // No mostramos el rol primario en la lista (siempre lo tiene)
  const options = EXTRA_ROLE_OPTIONS.filter((r) => r.value !== user.role);
  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="extra-roles-modal"
    >
      <div
        className="tactical-card w-full max-w-lg p-6 space-y-4"
        style={{ borderColor: "#22d3ee" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-cyan-400/10 border border-cyan-400 flex items-center justify-center flex-shrink-0">
            <Layers className="text-cyan-400" size={18}/>
          </div>
          <div>
            <div className="overline text-cyan-400">Multi-rol</div>
            <div className="font-display text-xl text-white leading-tight">
              ROLES ADICIONALES
            </div>
            <div className="text-[11px] text-[#A1A1AA] mt-1">
              {user.name} · rol primario: <span className="text-white font-bold">{ROLE_LABEL[user.role] || user.role}</span>
            </div>
          </div>
        </div>

        <div className="text-[12px] text-[#A1A1AA] leading-relaxed">
          Marcá los roles que este usuario podrá <span className="text-white">alternar</span> desde el menú superior derecho. Por ejemplo: un Admin Directivo que también juega puede activarse como "Jugador" cuando ve su ficha o paga sus cuotas.
        </div>

        <div className="grid grid-cols-2 gap-2">
          {options.map((r) => {
            const active = selected.includes(r.value);
            return (
              <button
                key={r.value}
                onClick={() => onToggle(r.value)}
                data-testid={`toggle-extra-role-${user.user_id}-${r.value}`}
                className={`flex items-center gap-2 px-3 py-2.5 border text-xs uppercase tracking-wider font-bold transition-colors ${
                  active
                    ? "border-cyan-400 bg-cyan-400/10 text-white"
                    : "border-[#27272A] text-[#A1A1AA] hover:border-[#52525b]"
                }`}
              >
                <span
                  className={`w-3 h-3 border ${active ? "bg-cyan-400 border-cyan-400" : "border-[#52525b]"} flex items-center justify-center`}
                >
                  {active && <span className="text-black text-[8px] font-black">✓</span>}
                </span>
                {r.label}
              </button>
            );
          })}
        </div>

        <div className="text-[10px] text-[#52525b] leading-snug">
          El rol primario se mantiene siempre. Super Admin tiene acceso a todos los roles automáticamente.
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="btn-ghost text-xs flex-1"
            data-testid="extra-roles-cancel"
          >
            CANCELAR
          </button>
          <button
            onClick={onSave}
            className="text-xs uppercase tracking-widest font-bold flex-1 py-2.5 transition-colors flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(90deg, #0891b2 0%, #06b6d4 100%)", color: "#0a0a0a" }}
            data-testid="extra-roles-save"
          >
            GUARDAR
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteUserModal({ user, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const expected = (user.name || user.email || "").toUpperCase().trim();
  const canDelete = typed.trim().toUpperCase() === expected && !busy;
  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="delete-user-modal"
    >
      <div
        className="tactical-card w-full max-w-md p-6 space-y-4"
        style={{ borderColor: "#FF3B30" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#FF3B30]/10 border border-[#FF3B30] flex items-center justify-center flex-shrink-0">
            <AlertOctagon className="text-[#FF3B30]" size={18}/>
          </div>
          <div>
            <div className="overline text-[#FF3B30]">Acción destructiva</div>
            <div className="font-display text-xl text-white leading-tight">ELIMINAR USUARIO</div>
          </div>
        </div>
        <div className="text-[12px] text-[#A1A1AA] leading-relaxed">
          Vas a eliminar a <strong className="text-white">{user.name}</strong> (<span className="font-mono">{user.email}</span>).
          <br/><br/>
          Se borrarán: <span className="text-white">perfil, sesiones activas, cuotas, comprobantes registrados, conexiones de calendario, RSVPs a eventos</span> y se desasignará de los depth charts.
          <br/><br/>
          <span className="text-cyan-300/90">Los archivos en Drive del club (fotos, comprobantes históricos) NO se borran</span> para preservar trazabilidad.
        </div>
        <div className="space-y-2">
          <label className="overline">
            Para confirmar, escribe <span className="text-[#FF3B30] font-mono">{expected}</span>:
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expected}
            className="input-tactical w-full"
            data-testid="delete-user-confirm-input"
            autoFocus
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={onCancel}
            className="btn-ghost text-xs flex-1"
            disabled={busy}
            data-testid="delete-user-cancel"
          >
            CANCELAR
          </button>
          <button
            onClick={onConfirm}
            disabled={!canDelete}
            className="text-xs uppercase tracking-widest font-bold flex-1 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: "#FF3B30", color: "white" }}
            data-testid="delete-user-confirm"
          >
            <Trash2 size={12}/> {busy ? "ELIMINANDO..." : "ELIMINAR"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Cell = ({ label, value, color }) => (
  <div className="p-5 border-r border-b border-[#27272A] last:border-r-0">
    <div className="overline text-[#71717A]">{label}</div>
    <div className="font-display text-3xl mt-1" style={{ color: color || "#fff" }}>
      {value}
    </div>
  </div>
);
